
/**
 * Module dependencies.
 */

import { color, media, units } from 'src/styles/utils';
import { theme } from 'styled-tools';
import Container from 'src/components/core/layout/container';
import React, { ReactElement, ReactNode } from 'react';
import Type from 'src/components/core/typography';
import styled from 'styled-components';

/**
 * `Props` type.
 */

type Props = {
  children: ReactNode,
  description: ReactElement | string,
  title: string
};

/**
 * `Section` styled component.
 */

const Section = styled.section`
  background-color: ${color('beige200')};
  overflow: hidden;
  padding: calc(${theme('dimensions.navbarHeightMobile')}px + ${units(5)}) 0 ${units(13)};
  position: relative;

  ${media.min('ms')`
    padding: calc(${theme('dimensions.navbarHeight')}px + ${units(10)}) 0 ${units(25)};
  `}
`;

/**
 * `Grid` styled component.
 */

const Grid = styled.div`
  ${media.min('ms')`
    display: grid;
    grid-template-areas:
      '. title       .'
      '. description .'
      '. content     .';
    grid-template-columns: 1fr 10fr 1fr;
  `}

  ${media.min('md')`
    grid-template-areas:
      '. title       . content .'
      '. description . content .'
      '. .           . content .';
    grid-template-columns: 1fr 5fr 1fr 4fr 1fr;
  `}
`;

/**
 * `Title` styled component.
 */

const Title = styled(Type.H2)`
  grid-area: title;

  ${media.min('md')`
    margin-bottom: ${units(6)};
  `}
`;

/**
 * `Description` styled component.
 */

const Description = styled(Type.H6).attrs({
  as: 'div',
  small: true
})`
  color: ${color('grey400')};
  grid-area: description;

  ul {
    list-style: none;
    margin: 0 0 1em;
    padding: 0;

    li::before {
      content: '- ';
    }
  }

  ${media.max('ms')`
    padding-top: ${units(6)};
  `}
`;

/**
 * `Content` styled component.
 */

const Content = styled.div`
  grid-area: content;
  padding-top: ${units(6)};
`;

/**
 * `AuthBaseLayout` page.
 */

const AuthBaseLayout = (props: Props): ReactElement => {
  const { children, description, title } = props;

  return (
    <Section>
      <Container>
        <Grid>
          <Title as={'h1'}>
            {title}
          </Title>

          <Content>
            {children}
          </Content>

          <Description>
            {description}
          </Description>
        </Grid>
      </Container>
    </Section>
  );
};

/**
 * Export `AuthBaseLayout` page.
 */

export default AuthBaseLayout;
